import React from "react";
import type { SettingDefinition } from "@app/services/adminSettingsApi";
import { Card } from "@app/components/shared/Card";

interface AiEngineSettingsSectionProps {
  sectionName: string;
  sectionData: { [subsection: string]: SettingDefinition[] };
  pendingChanges: Map<string, any>;
  onSettingChange: (key: string, value: any) => void;
  onReset: () => void;
}

const GROUP_MAP: Record<string, string[]> = {
  "Generation Settings": [
    "ai_engine.min_confidence_to_queue",
    "ai_engine.min_estimated_gain_pct",
    "ai_engine.require_timeframe_alignment",
    "ai_engine.required_timeframes",
    "ai_engine.confidence_decay_start_after_minutes",
    "ai_engine.confidence_decay_floor",
    "ai_engine.confidence_decay_enabled",
    "ai_engine.confidence_decay_rate",
  ],
  "LLM & API Settings": [
    "ai_engine.max_daily_api_calls",
    "ai_engine.max_calls_per_minute",
    "ai_engine.rate_limit_per_minute",
    "ai_engine.llm_provider",
    "ai_engine.generation_cycle_seconds",
    "ai_engine.api_call_timeout_seconds",
  ],
  "Queue Re-evaluation Settings": [
    "ai_engine.queue_reeval_enabled",
    "ai_engine.queue_keep_threshold",
    "ai_engine.queue_remove_threshold",
  ],
  "Shadow Mode & Comparison Settings": [
    "ai_engine.shadow_mode",
    "ai_engine.shadow_mode_min_days",
    "ai_engine.shadow_mode_min_trades",
    "ai_engine.shadow_mode_min_win_rate",
    "ai_engine.log_rule_conflicts",
    "ai_engine.prediction_accuracy_correction",
  ],
  "Custom Instructions": [
    "ai_engine.custom_instructions_enabled",
    "ai_engine.custom_instructions_text",
  ],
  "Learning & Evolution Settings": [
    "ai_engine.learning_enabled",
    "ai_engine.auto_prompt_update_enabled",
    "ai_engine.prompt_min_sample_size",
    "ai_engine.prompt_min_pattern_pct",
  ],
  "Active Mode & Risk Settings": [
    "ai_engine.enabled",
    "ai_engine.fallback_on_failure",
    "ai_engine.target_mode",
    "ai_engine.pair_discovery_max_suggestions",
    "ai_engine.flexible_target_enabled",
  ],
  "Miscellaneous Settings": [
    "ai_engine.context_trades_count",
    "ai_engine.recommendation.engine.mode",
  ],
};

function extractGroupSettings(
  groupKeys: string[],
  allSettings: SettingDefinition[],
): SettingDefinition[] {
  const keysSet = new Set(groupKeys);
  return allSettings.filter((s) => keysSet.has(s.key));
}

export function AiEngineSettingsSection({
  sectionName,
  sectionData,
  pendingChanges,
  onSettingChange,
  onReset,
}: AiEngineSettingsSectionProps): JSX.Element {
  // Flatten all settings from all subsections
  const allSettings: SettingDefinition[] = Object.values(sectionData).flatMap(
    (arr) => (Array.isArray(arr) ? arr : []),
  );

  // Filter out recommendation.engine.mode from Miscellaneous since it's handled by Engine Mode selector
  const groupEntries = Object.entries(GROUP_MAP).map(([groupName, keys]) => {
    const settings = keys === GROUP_MAP["Miscellaneous Settings"]
      ? extractGroupSettings(keys, allSettings).filter((s) => s.key !== "recommendation.engine.mode")
      : extractGroupSettings(keys, allSettings);
    return [groupName, settings] as const;
  });

  return (
    <div className="settings-section">
      <div className="settings-section__header">
        <h2>{sectionName}</h2>
        <div className="settings-section__header-actions">
          <button
            className="reset-button"
            onClick={onReset}
            style={{
              padding: "8px 16px",
              borderRadius: "6px",
              border: "1px solid rgba(239, 68, 68, 0.3)",
              background: "transparent",
              color: "rgba(239, 68, 68, 0.9)",
              cursor: "pointer",
              fontWeight: 500,
              fontSize: "0.85rem",
            }}
          >
            Reset to Defaults
          </button>
        </div>
      </div>

      {groupEntries.map(([groupName, settings]) => {
        if (settings.length === 0) return null;

        return (
          <Card key={groupName} style={{ marginBottom: "16px" }}>
            <div style={{ padding: "20px" }}>
              <h3
                style={{
                  fontSize: "1rem",
                  fontWeight: 600,
                  color: "rgba(191, 219, 254, 0.9)",
                  marginBottom: "16px",
                  paddingBottom: "8px",
                  borderBottom: "1px solid rgba(148, 163, 184, 0.15)",
                }}
              >
                {groupName}
              </h3>
              <div className="settings-grid">
                {settings.map((setting) => (
                  <SettingField
                    key={setting.key}
                    setting={setting}
                    value={
                      pendingChanges.get(setting.key) ??
                      setting.current_value ??
                      setting.default_value
                    }
                    onChange={(value) => onSettingChange(setting.key, value)}
                  />
                ))}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

interface SettingFieldProps {
  setting: SettingDefinition;
  value: any;
  onChange: (value: any) => void;
}

function SettingField({ setting, value, onChange }: SettingFieldProps): JSX.Element {
  const [localValue, setLocalValue] = React.useState<string>(
    value != null ? String(value) : ""
  );

  React.useEffect(() => {
    setLocalValue(value != null ? String(value) : "");
  }, [value]);

  if (setting.is_readonly) {
    return (
      <div className="setting-input setting-input--readonly">
        <label>{setting.label}</label>
        <input type="text" value={value} readOnly />
        <span className="setting-input__readonly-badge">Read-only</span>
      </div>
    );
  }

  const handleBlur = () => {
    if (setting.type === "int") {
      const parsed = parseInt(localValue, 10);
      if (!isNaN(parsed)) {
        onChange(parsed);
        setLocalValue(String(parsed));
      }
    } else if (setting.type === "float") {
      const parsed = parseFloat(localValue);
      if (!isNaN(parsed)) {
        onChange(parsed);
        setLocalValue(String(parsed));
      }
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    if (setting.type === "bool") {
      onChange((e.target as HTMLInputElement).checked);
      return;
    }

    if (setting.type === "select") {
      onChange(e.target.value);
      return;
    }

    if (setting.type === "string") {
      onChange(e.target.value);
      return;
    }

    setLocalValue(e.target.value);
  };

  return (
    <div className={`setting-input setting-input--${setting.type}`}>
      <label>{setting.label}</label>

      {setting.description && (
        <p
          style={{
            fontSize: "0.78rem",
            color: "rgba(148, 163, 184, 0.7)",
            margin: "2px 0 8px 0",
            lineHeight: 1.4,
          }}
        >
          {setting.description}
        </p>
      )}

      {setting.is_dangerous && setting.warning_text && (
        <p
          style={{
            fontSize: "0.8rem",
            color: "rgb(250, 204, 21)",
            margin: "0 0 8px 0",
          }}
        >
          {setting.warning_text}
        </p>
      )}

      {setting.type === "bool" ? (
        <label className="setting-input__checkbox-label">
          <input
            type="checkbox"
            checked={value ?? false}
            onChange={handleChange}
          />
          <span>{setting.label}</span>
        </label>
      ) : setting.type === "select" && setting.constraints?.options ? (
        <select value={value ?? ""} onChange={handleChange}>
          {(
            setting.constraints.options as (
              | string
              | number
              | { value: string | number; label: string }
            )[]
          ).map((option) => {
            const optValue =
              typeof option === "object" ? option.value : option;
            const optLabel =
              typeof option === "object" ? option.label : option;
            return (
              <option key={optValue} value={optValue}>
                {optLabel}
              </option>
            );
          })}
        </select>
      ) : setting.type === "string" ? (
        <textarea
          value={value ?? ""}
          onChange={handleChange}
          rows={4}
          style={{
            width: "100%",
            padding: "8px 12px",
            borderRadius: "6px",
            border: "1px solid rgba(148, 163, 184, 0.25)",
            background: "rgba(30, 41, 59, 0.5)",
            color: "rgba(226, 232, 240, 0.9)",
            fontSize: "0.85rem",
            fontFamily: "inherit",
            resize: "vertical",
          }}
        />
      ) : setting.type === "int" || setting.type === "float" ? (
        <input
          type="text"
          inputMode="decimal"
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          style={{
            fontVariantNumeric: "tabular-nums",
          }}
        />
      ) : (
        <input
          type="text"
          value={value ?? ""}
          onChange={handleChange}
        />
      )}

      <div className="setting-input__meta">
        <span className="setting-input__default">
          Default: {JSON.stringify(setting.default_value)}
        </span>
      </div>
    </div>
  );
}
